import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Swords, Trophy, Crown, Users2, CalendarClock, Loader2, Plus, Save,
  Flame, Star, Send, Settings2, X,
} from "lucide-react";

const KPIS = [
  { key: "contactos", label: "Contactos", action: "contacto" },
  { key: "reunioes", label: "Reuniões", action: "reuniao" },
  { key: "angariacoes", label: "Angariações", action: "angariacao" },
  { key: "vendas", label: "Vendas", action: "venda" },
  { key: "posts", label: "Posts redes sociais", action: "post" },
  { key: "videos", label: "Vídeos humanizados", action: "video" },
];

const ARCH_STYLE = {
  analista: { color: "#3B6EFF", emoji: "🧠" },
  diplomata: { color: "#F7861F", emoji: "🤝" },
  sentinela: { color: "#22C55E", emoji: "🛡️" },
  explorador: { color: "#A855F7", emoji: "🧭" },
};

export default function Liga() {
  const { user, refresh } = useAuth();
  const canManage = user.role === "admin" || user.role === "agency_manager";
  const isMember = ["consultant", "staff", "agency_manager"].includes(user.role);

  const [seasons, setSeasons] = useState(null);
  const [season, setSeason] = useState(null);
  const [lb, setLb] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [tab, setTab] = useState("individual");
  const [kpis, setKpis] = useState(Object.fromEntries(KPIS.map((k) => [k.key, 0])));
  const [saving, setSaving] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const loadSeasons = async () => {
    const { data } = await api.get("/seasons");
    setSeasons(data);
    const active = data.find((s) => s.status === "active") || data[0] || null;
    setSeason(active);
    return active;
  };

  const loadSeasonData = async (s) => {
    if (!s) { setLb(null); return; }
    const [lbRes, meRes] = await Promise.all([
      api.get(`/seasons/${s.id}/leaderboard`),
      api.get(`/seasons/${s.id}/my-entries`),
    ]);
    setLb(lbRes.data);
    setMyEntries(meRes.data);
  };

  useEffect(() => {
    (async () => {
      const active = await loadSeasons();
      await loadSeasonData(active);
      const t = await api.get("/teams");
      setTeams(t.data);
      if (canManage) {
        try { const u = await api.get("/users"); setMembers(u.data.filter((m) => ["consultant", "staff", "agency_manager"].includes(m.role))); } catch (e) {}
      }
    })();
  }, []);

  // preload current week entry into the form
  useEffect(() => {
    if (!season) return;
    const cur = myEntries.find((e) => e.week === season.current_week);
    setKpis(cur ? { ...Object.fromEntries(KPIS.map((k) => [k.key, cur.kpis?.[k.key] ?? 0])) } : Object.fromEntries(KPIS.map((k) => [k.key, 0])));
  }, [season, myEntries]);

  const previewPoints = useMemo(() => {
    if (!season) return 0;
    const cfg = season.points_config || {};
    return KPIS.reduce((sum, k) => sum + (Number(kpis[k.key]) || 0) * (Number(cfg[k.action]) || 0), 0);
  }, [kpis, season]);

  const submitEntry = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/seasons/${season.id}/weekly-entry`, { week: season.current_week, kpis });
      toast.success(`Registo da Semana ${data.week} guardado! +${data.points} pts`);
      await loadSeasonData(season);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  if (seasons === null) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6" data-testid="liga-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
            <Swords className="text-brand-orange" /> Liga
          </h1>
          <p className="text-brand-muted text-sm mt-1">Competimos para crescer — reporta a tua semana e sobe no ranking da edição.</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowManage((v) => !v)} data-testid="toggle-manage-btn"
            className="bg-brand-surface border border-brand-border hover:bg-white/5 text-white rounded-sm font-bold">
            <Settings2 size={16} className="mr-1" /> Gerir edição & equipas
          </Button>
        )}
      </div>

      {!season ? (
        <div className="rounded-sm bg-brand-surface border border-brand-border p-10 text-center">
          <Swords size={40} className="mx-auto mb-3 text-brand-muted opacity-50" />
          <p className="text-brand-muted">Ainda não há nenhuma edição ativa.</p>
          {canManage && <p className="text-sm text-brand-muted mt-2">Cria a primeira edição em "Gerir edição & equipas".</p>}
        </div>
      ) : (
        <>
          {/* Season header */}
          <div className="rounded-sm bg-gradient-to-br from-brand-blue/15 to-brand-orange/10 border border-brand-border p-5 sm:p-6 flex flex-wrap items-center gap-6" data-testid="season-header">
            <div className="flex-1 min-w-[200px]">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">{season.edition || "Edição"}</span>
              <h2 className="font-heading font-black text-xl sm:text-2xl mt-1">{season.name}</h2>
            </div>
            <div className="text-center px-4 border-l border-brand-border">
              <p className="font-heading font-black text-3xl text-white">{season.current_week}<span className="text-brand-muted text-lg">/{season.total_weeks}</span></p>
              <p className="text-xs text-brand-muted uppercase tracking-wider">Semana</p>
            </div>
            <div className="text-center px-4 border-l border-brand-border">
              <p className="font-heading font-black text-xl text-white flex items-center gap-1"><CalendarClock size={18} className="text-brand-orange" /> {season.days_left}d {season.hours_left}h</p>
              <p className="text-xs text-brand-muted uppercase tracking-wider">Para o fim da semana</p>
            </div>
          </div>

          {/* MVPs */}
          {lb && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MvpCard title="MVP da Edição" icon={<Crown size={18} className="text-yellow-400" />} entry={lb.mvp_edition} accent="#EAB308" />
              <MvpCard title="MVP da Semana" icon={<Flame size={18} className="text-brand-orange" />} entry={lb.mvp_week} accent="#F7861F" />
            </div>
          )}
          {lb && Object.values(lb.archetype_mvps || {}).some(Boolean) && (
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-2">MVPs por arquétipo · Semana {season.current_week}</p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {["analista", "diplomata", "sentinela", "explorador"].map((arch) => {
                  const e = lb.archetype_mvps[arch];
                  const st = ARCH_STYLE[arch];
                  return (
                    <div key={arch} data-testid={`archetype-${arch}`} className="rounded-sm bg-brand-surface border border-brand-border p-3" style={{ borderTopColor: st.color, borderTopWidth: 2 }}>
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: st.color }}>{st.emoji} {lb.archetype_labels[arch]}</p>
                      {e ? (
                        <div className="mt-1"><p className="font-semibold text-sm truncate">{e.name}</p><p className="text-xs text-brand-muted">{e.mbti} · {e.points} pts</p></div>
                      ) : <p className="text-xs text-brand-muted mt-1">Sem registos</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Weekly entry */}
          {isMember && (
            <div className="rounded-sm bg-brand-surface border border-brand-border p-5" data-testid="weekly-entry-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Send size={18} className="text-brand-blue" /> Registo da Semana {season.current_week}</h3>
                <span className="text-sm text-brand-muted">Pontos desta semana: <b className="text-brand-orange">{previewPoints}</b></span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                {KPIS.map((k) => (
                  <div key={k.key}>
                    <Label className="text-xs text-brand-muted">{k.label} <span className="text-brand-orange">(+{season.points_config?.[k.action] ?? 0})</span></Label>
                    <Input type="number" min="0" value={kpis[k.key]} data-testid={`kpi-${k.key}`}
                      onChange={(e) => setKpis({ ...kpis, [k.key]: e.target.value })}
                      className="mt-1 bg-background border-brand-border rounded-sm" />
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-4">
                <Button onClick={submitEntry} disabled={saving} data-testid="submit-weekly-btn"
                  className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Guardar registo (+{previewPoints} pts)
                </Button>
              </div>
            </div>
          )}

          {/* Ranking */}
          {lb && (
            <div>
              <div className="inline-flex rounded-sm border border-brand-border overflow-hidden mb-3" data-testid="ranking-tabs">
                {[["individual", "Individual"], ["equipas", "Equipas"]].map(([v, l]) => (
                  <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${tab === v ? "bg-brand-blue text-white" : "text-brand-muted hover:text-white"}`}>{l}</button>
                ))}
              </div>

              {tab === "individual" ? (
                <div className="rounded-sm bg-brand-surface border border-brand-border overflow-hidden">
                  {lb.individual.map((e, i) => (
                    <motion.div key={e.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                      data-testid="individual-row"
                      className={`flex items-center gap-4 px-4 py-3 border-b border-brand-border last:border-0 ${e.is_me ? "bg-brand-blue/10" : ""}`}>
                      <span className="w-6 text-center font-heading font-bold text-brand-muted">{e.rank}</span>
                      <div className="h-9 w-9 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-sm font-bold text-white shrink-0">{e.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{e.name}{e.is_me && <span className="text-brand-blue"> (tu)</span>}</p>
                        <p className="text-xs text-brand-muted">{e.mbti ? `${e.mbti} · ${ARCH_STYLE[e.archetype]?.emoji || ""} ${(lb.archetype_labels?.[e.archetype]) || ""}` : "—"}</p>
                      </div>
                      <span className="font-heading font-bold text-brand-orange">{e.points}</span>
                    </motion.div>
                  ))}
                  {lb.individual.length === 0 && <p className="p-8 text-center text-brand-muted">Sem participantes.</p>}
                </div>
              ) : (
                <div className="rounded-sm bg-brand-surface border border-brand-border overflow-hidden">
                  {lb.teams.map((t, i) => (
                    <div key={t.id} data-testid="team-row" className="flex items-center gap-4 px-4 py-3 border-b border-brand-border last:border-0">
                      <span className="w-6 text-center font-heading font-bold text-brand-muted">{t.rank}</span>
                      <div className="h-9 w-9 rounded-sm bg-brand-blue/15 text-brand-blue flex items-center justify-center shrink-0"><Users2 size={16} /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{t.name}</p>
                        <p className="text-xs text-brand-muted">{t.members} membros</p>
                      </div>
                      <span className="font-heading font-bold text-brand-orange">{t.points}</span>
                    </div>
                  ))}
                  {lb.teams.length === 0 && <p className="p-8 text-center text-brand-muted">Sem equipas. {canManage && "Cria equipas em 'Gerir edição & equipas'."}</p>}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showManage && canManage && (
        <ManagePanel
          season={season} teams={teams} members={members}
          onClose={() => setShowManage(false)}
          onChanged={async () => {
            const active = await loadSeasons();
            await loadSeasonData(active);
            const t = await api.get("/teams"); setTeams(t.data);
          }}
        />
      )}
    </div>
  );
}

function MvpCard({ title, icon, entry, accent }) {
  return (
    <div className="rounded-sm bg-brand-surface border border-brand-border p-5 flex items-center gap-4" style={{ borderLeftColor: accent, borderLeftWidth: 3 }} data-testid={`mvp-${title.includes("Edição") ? "edition" : "week"}`}>
      <div className="h-12 w-12 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${accent}22` }}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-brand-muted">{title}</p>
        {entry ? (
          <><p className="font-heading font-bold text-lg truncate">{entry.name}</p>
            <p className="text-sm" style={{ color: accent }}>{entry.points} pts{entry.mbti ? ` · ${entry.mbti}` : ""}</p></>
        ) : <p className="text-sm text-brand-muted mt-1">Ainda sem MVP</p>}
      </div>
    </div>
  );
}

function ManagePanel({ season, teams, members, onClose, onChanged }) {
  const [name, setName] = useState(season?.name || "A4G League");
  const [edition, setEdition] = useState(season?.edition || "Edição de Outono");
  const [weeks, setWeeks] = useState(season?.weeks || 10);
  const [startDate, setStartDate] = useState((season?.start_date || new Date().toISOString()).slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [newTeam, setNewTeam] = useState("");
  const [teamState, setTeamState] = useState(() => Object.fromEntries((teams || []).map((t) => [t.id, (t.members || []).map((m) => m.id)])));

  const saveSeason = async () => {
    setBusy(true);
    try {
      const payload = { name, edition, weeks: Number(weeks), start_date: new Date(startDate).toISOString(), status: "active" };
      if (season) await api.put(`/seasons/${season.id}`, payload);
      else await api.post("/seasons", payload);
      toast.success("Edição guardada");
      await onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };

  const createTeam = async () => {
    if (!newTeam.trim()) return;
    try { await api.post("/teams", { name: newTeam.trim(), member_ids: [] }); setNewTeam(""); toast.success("Equipa criada"); await onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleMember = (teamId, mid) => {
    setTeamState((s) => {
      const cur = s[teamId] || [];
      return { ...s, [teamId]: cur.includes(mid) ? cur.filter((x) => x !== mid) : [...cur, mid] };
    });
  };

  const saveTeam = async (t) => {
    try { await api.put(`/teams/${t.id}`, { name: t.name, member_ids: teamState[t.id] || [] }); toast.success(`Equipa "${t.name}" atualizada`); await onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const removeTeam = async (t) => {
    try { await api.delete(`/teams/${t.id}`); toast.success("Equipa removida"); await onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manage-panel">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-sm bg-brand-surface border border-brand-border">
        <div className="sticky top-0 bg-brand-surface/95 backdrop-blur border-b border-brand-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Settings2 size={18} className="text-brand-orange" /> Gerir edição & equipas</h2>
          <button onClick={onClose} className="text-brand-muted hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-8">
          <div>
            <h3 className="font-heading font-bold mb-3">Edição {season ? "atual" : "(nova)"}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs text-brand-muted">Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="season-name" className="mt-1 bg-background border-brand-border rounded-sm" /></div>
              <div className="col-span-2"><Label className="text-xs text-brand-muted">Edição</Label><Input value={edition} onChange={(e) => setEdition(e.target.value)} data-testid="season-edition" className="mt-1 bg-background border-brand-border rounded-sm" /></div>
              <div><Label className="text-xs text-brand-muted">Nº de semanas</Label><Input type="number" min="1" value={weeks} onChange={(e) => setWeeks(e.target.value)} data-testid="season-weeks" className="mt-1 bg-background border-brand-border rounded-sm" /></div>
              <div><Label className="text-xs text-brand-muted">Data de início</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="season-start" className="mt-1 bg-background border-brand-border rounded-sm" /></div>
            </div>
            <Button onClick={saveSeason} disabled={busy} data-testid="save-season-btn" className="mt-3 bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Guardar edição
            </Button>
          </div>

          <div className="border-t border-brand-border pt-6">
            <h3 className="font-heading font-bold mb-3 flex items-center gap-2"><Users2 size={16} /> Equipas</h3>
            <div className="flex gap-2 mb-4">
              <Input value={newTeam} onChange={(e) => setNewTeam(e.target.value)} placeholder="Nome da nova equipa" data-testid="new-team-name" className="bg-background border-brand-border rounded-sm" />
              <Button onClick={createTeam} data-testid="create-team-btn" className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold shrink-0"><Plus size={16} className="mr-1" /> Criar</Button>
            </div>
            <div className="space-y-4">
              {(teams || []).map((t) => (
                <div key={t.id} className="rounded-sm border border-brand-border p-4" data-testid={`team-manage-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{t.name}</p>
                    <button onClick={() => removeTeam(t)} className="text-xs text-destructive hover:underline">Remover</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {members.map((m) => {
                      const on = (teamState[t.id] || []).includes(m.id);
                      return (
                        <button key={m.id} onClick={() => toggleMember(t.id, m.id)}
                          className={`text-left text-sm px-3 py-1.5 rounded-sm border transition-colors ${on ? "border-brand-blue bg-brand-blue/15 text-white" : "border-brand-border text-brand-muted hover:text-white"}`}>
                          {m.name}
                        </button>
                      );
                    })}
                    {members.length === 0 && <p className="text-xs text-brand-muted col-span-2">Sem membros na agência.</p>}
                  </div>
                  <Button onClick={() => saveTeam(t)} className="mt-3 bg-brand-surface border border-brand-border hover:bg-white/5 text-white rounded-sm text-sm font-bold"><Save size={14} className="mr-1" /> Guardar membros</Button>
                </div>
              ))}
              {(teams || []).length === 0 && <p className="text-sm text-brand-muted">Ainda não há equipas.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
